import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuditStatus, fetchAnalysisEvents, startAnalysis } from '../api/client';
import { connectSSE, type SSECallbacks } from '../api/sse';
import { createAnalysisConnection, type ConnectionStatus, type AnalysisConnection } from '../api/centrifugo';
import { useProcessingStore } from '../stores/useProcessingStore';
import { useAuthStore } from '../stores/useAuthStore';
import { replayAnalysisEvents } from '../utils/replayEvents';

const ACTIVE_ANALYSIS_KEY = 'active_analysis';
const STALE_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours

interface ActiveAnalysis {
  chatId: string;
  startedAt: number;
  profile: string;
}

// Centrifugo feature flag — reads from env at build time
const CENTRIFUGO_ENABLED = import.meta.env.VITE_CENTRIFUGO_ENABLED === 'true';

/** Persist analysis session to localStorage for cross-reload survival. */
export function setActiveAnalysis(chatId: string, profile: string) {
  const entry: ActiveAnalysis = { chatId, startedAt: Date.now(), profile };
  localStorage.setItem(ACTIVE_ANALYSIS_KEY, JSON.stringify(entry));
}

/** Clear active analysis from localStorage (on completion). */
export function clearActiveAnalysis() {
  localStorage.removeItem(ACTIVE_ANALYSIS_KEY);
}

/** Read active analysis from localStorage (returns null if stale or missing). */
function getActiveAnalysis(): ActiveAnalysis | null {
  try {
    const raw = localStorage.getItem(ACTIVE_ANALYSIS_KEY);
    if (!raw) return null;
    const entry: ActiveAnalysis = JSON.parse(raw);
    if (Date.now() - entry.startedAt > STALE_THRESHOLD_MS) {
      localStorage.removeItem(ACTIVE_ANALYSIS_KEY);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export interface UseAnalysisConnectionOptions {
  /** Chat ID to connect to. If null, tries to restore from localStorage/server. */
  chatId: string | null;
  /** SSE callbacks for live event dispatch. */
  callbacks: SSECallbacks;
  /** Message to send when starting a NEW analysis (not restore). */
  message?: string;
  /** Profile for analysis (e.g. 'client-analysis-v2'). */
  profile?: string;
  /** Extra context for the chat request. */
  context?: Record<string, string>;
  /** Called when keepalive is received (SSE mode). */
  onKeepalive?: () => void;
}

export interface UseAnalysisConnectionResult {
  /** Whether state is being restored from persisted events. */
  isRestoring: boolean;
  /** Real-time connection status (only meaningful in Centrifugo mode). */
  connectionStatus: ConnectionStatus;
  /** The resolved chat ID (may come from localStorage restore). */
  resolvedChatId: string | null;
  /** Start a new analysis on the given chatId. */
  startNewAnalysis: (chatId: string, message: string, profile: string, ctx?: Record<string, string>) => void;
}

/**
 * Hook that manages the real-time connection for analysis.
 *
 * - On mount, checks localStorage → server for an in-progress analysis
 * - If found, replays persisted events to restore UI state, then subscribes to live updates
 * - If not found, waits for `startNewAnalysis` to be called
 * - Supports both SSE (legacy) and Centrifugo (when enabled)
 */
export function useAnalysisConnection(
  opts: UseAnalysisConnectionOptions,
): UseAnalysisConnectionResult {
  const [isRestoring, setIsRestoring] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [resolvedChatId, setResolvedChatId] = useState<string | null>(opts.chatId);

  const navigate = useNavigate();
  const token = useAuthStore.getState().token;
  const disconnectRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(true);

  // Wrap callbacks to clear active analysis on completion
  const wrapCallbacks = useCallback((cb: SSECallbacks): SSECallbacks => ({
    ...cb,
    onDone: () => {
      clearActiveAnalysis();
      cb.onDone?.();
    },
    onError: (data) => {
      cb.onError?.(data);
    },
  }), []);

  // Connect via Centrifugo
  const connectCentrifugo = useCallback(async (chatId: string, cb: SSECallbacks) => {
    try {
      const conn: AnalysisConnection = await createAnalysisConnection(
        chatId,
        wrapCallbacks(cb),
        setConnectionStatus,
        (recovered) => {
          if (!recovered) {
            // Recovery failed — need to replay from DB
            fetchAnalysisEvents(chatId, 0).then(({ events }) => {
              if (events.length > 0) {
                replayAnalysisEvents(events);
              }
            }).catch(() => { /* best effort */ });
          }
        },
      );
      disconnectRef.current = conn.disconnect;
    } catch {
      setConnectionStatus('disconnected');
    }
  }, [wrapCallbacks]);

  // Connect via SSE (legacy)
  const connectSSELegacy = useCallback((
    chatId: string,
    message: string,
    profile: string,
    cb: SSECallbacks,
    ctx?: Record<string, string>,
  ) => {
    const cancel = connectSSE(
      chatId, message, profile,
      wrapCallbacks(cb),
      token ?? undefined,
      opts.onKeepalive,
      ctx,
    );
    disconnectRef.current = cancel;
    setConnectionStatus('connected');
  }, [token, opts.onKeepalive, wrapCallbacks]);

  // Start a new analysis
  const startNewAnalysis = useCallback((
    chatId: string,
    message: string,
    profile: string,
    ctx?: Record<string, string>,
  ) => {
    setResolvedChatId(chatId);
    setActiveAnalysis(chatId, profile);

    if (CENTRIFUGO_ENABLED) {
      // Background trigger + WebSocket subscription
      startAnalysis(chatId, message, profile, ctx).catch(() => {
        opts.callbacks.onError?.({ message: 'Failed to start analysis' });
      });
      connectCentrifugo(chatId, opts.callbacks);
    } else {
      // SSE mode
      connectSSELegacy(chatId, message, profile, opts.callbacks, ctx);
    }
  }, [opts.callbacks, connectCentrifugo, connectSSELegacy]);

  // On mount: try to restore from localStorage → server fallback
  useEffect(() => {
    mountedRef.current = true;

    const tryRestore = async () => {
      // If chatId provided directly, don't auto-restore
      if (opts.chatId) {
        setResolvedChatId(opts.chatId);
        return;
      }

      // 1. Check localStorage (fast path)
      const active = getActiveAnalysis();
      const candidateId = active?.chatId ?? null;

      if (!candidateId) return;

      setIsRestoring(true);

      try {
        // 2. Query server status
        const status = await fetchAuditStatus(candidateId);
        if (!mountedRef.current) return;

        if (status.is_complete) {
          // Analysis finished while we were away — go to report. (This used to
          // navigate to /questionnaire/:chatId, a leftover from the flow where
          // the questionnaire came AFTER analysis; it contradicted the comment
          // and, post-reorder, would have sent a finished run back to Confirm.)
          clearActiveAnalysis();
          navigate(`/report/${candidateId}`, { replace: true });
          return;
        }

        if (status.has_error && !status.is_running) {
          // Failed analysis — clear and don't restore
          clearActiveAnalysis();
          setIsRestoring(false);
          return;
        }

        // 3. Replay persisted events to restore UI state
        const { events } = await fetchAnalysisEvents(candidateId);
        if (!mountedRef.current) return;

        if (events.length > 0) {
          // Initialize pipeline first
          useProcessingStore.getState().initPipeline();
          const { isComplete } = replayAnalysisEvents(events);

          if (isComplete) {
            clearActiveAnalysis();
            navigate(`/report/${candidateId}`, { replace: true });
            return;
          }
        }

        setResolvedChatId(candidateId);

        // 4. Subscribe to live updates if analysis is still running
        if (status.is_running) {
          if (CENTRIFUGO_ENABLED) {
            await connectCentrifugo(candidateId, opts.callbacks);
          }
          // SSE mode can't reconnect to an in-progress analysis (it's POST-triggered)
          // The analysis is still running server-side; events will continue via Centrifugo
        }
      } catch {
        // Restore failed — clear stale entry
        clearActiveAnalysis();
      } finally {
        if (mountedRef.current) setIsRestoring(false);
      }
    };

    tryRestore();

    return () => {
      mountedRef.current = false;
      disconnectRef.current?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isRestoring,
    connectionStatus,
    resolvedChatId,
    startNewAnalysis,
  };
}
